import { z } from 'zod';
import { PCMP_DIVISIONS } from './pcmpQueue.types';

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

const positiveInt = z.number().int().positive().optional().catch(undefined);

export const PCMP_MO_SCOPES = ['MO', 'ALL'] as const;
export type PcmpMoScope = (typeof PCMP_MO_SCOPES)[number];

/** The filters of the MO Wise report; which apply depends on the scope. */
export interface PcmpMoReportFilters {
  scope: PcmpMoScope;
  /** The MO, for scope MO. */
  moUserId?: number | undefined;
  /** The MO's direct manager, for scope ALL. */
  reportingUserId?: number | undefined;
  /** Region, for scope ALL. */
  departmentId?: number | undefined;
  /** For scope ALL. */
  division?: (typeof PCMP_DIVISIONS)[number] | undefined;
  projectIds?: number[] | undefined;
  dateFrom?: string | undefined;
  dateTo?: string | undefined;
}

export const pcmpMoReportSearchSchema = z.object({
  scope: z.enum(PCMP_MO_SCOPES).optional().catch(undefined),
  moUserId: positiveInt,
  reportingUserId: positiveInt,
  departmentId: positiveInt,
  division: z.enum(PCMP_DIVISIONS).optional().catch(undefined),
  projectIds: z.array(z.number().int().positive()).max(100).optional().catch(undefined),
  dateFrom: z.string().regex(DATE_ONLY_RE).optional().catch(undefined),
  dateTo: z.string().regex(DATE_ONLY_RE).optional().catch(undefined),
});

export type PcmpMoReportSearch = z.infer<typeof pcmpMoReportSearchSchema>;

const blockSchema = z.object({
  processing: z.number(),
  notVerified: z.number(),
  disbursed: z.number(),
  hold: z.number(),
  total: z.number(),
  return: z.number(),
});

export type PcmpMoBlockFigures = z.infer<typeof blockSchema>;

const figuresSchema = z.object({
  proposalQty: z.number(),
  instituteQty: z.number(),
  mkt: blockSchema,
  acc: blockSchema,
  totalDisbursed: z.number(),
  totalReturn: z.number(),
  grandTotal: z.number(),
});

export type PcmpMoFigures = z.infer<typeof figuresSchema>;

const moInfoSchema = z.object({
  userId: z.coerce.number(),
  name: z.string(),
  hrId: z.string().nullable(),
  mobile: z.string().nullable(),
  location: z.string().nullable(),
  department: z.string(),
  divisionCode: z.enum(PCMP_DIVISIONS).nullable(),
  division: z.string(),
  reporting: z.string().nullable(),
});

export type PcmpMoInfo = z.infer<typeof moInfoSchema>;

export const pcmpMoReportSchema = z.object({
  scope: z.enum(PCMP_MO_SCOPES),
  mo: moInfoSchema.nullable(),
  projects: z.array(z.object({ id: z.coerce.number(), name: z.string() })),
  byProject: z.array(z.object({ projectId: z.coerce.number(), figures: figuresSchema })),
  lines: z.array(z.object({ mo: moInfoSchema, figures: figuresSchema })),
  total: figuresSchema,
  scopeLabel: z.string(),
  periodLabel: z.string(),
  projectLabel: z.string(),
  reportDate: z.string(),
  /** Set when the scope needs a pick that is missing; nothing was read. */
  message: z.string().nullable(),
});

export type PcmpMoReport = z.infer<typeof pcmpMoReportSchema>;
